import React from 'react';
import { Box, Grid, PropTypes } from '@material-ui/core';
import * as Assets from '@assets';
import {
  CustomizedButton,
  CustomizedTypography,
  Stack
} from '@components/Customized';
import {
  CapaQuotationDetailedMaterialType,
  CapaQuotationDetailedQuotationItem
} from '@generated/graphql';
import AppConfig from '@config';
import { formatCurrency } from '@utils/formatter';
import { requestEnumToString } from '@utils/enumToString';
import { useStyles } from './ReceivedQuotationNormalForm';
import useFiles from '@hooks/useFiles';
import { Download } from '@carbon/icons-react';
import { colorSet } from '@components/Provider';
import { useLocation } from 'react-router';

interface QuotatonViewItemsTableProps {
  matches: boolean;
  items: CapaQuotationDetailedQuotationItem[];
  noDrawing: boolean;
  quotationId?: string;
  projectName?: string;
}

const QuotatonViewItemsTable = ({
  items,
  noDrawing,
  quotationId,
  projectName
}: QuotatonViewItemsTableProps) => {
  const classes = useStyles();
  const { onDownloadPdf } = useFiles();
  const { search } = useLocation();
  const isCreatePurchaseOrderPage = search.includes('create_purchaseorder');

  return (
    <Box>
      <Stack alignItems="center" justifyContent="space-between">
        <CustomizedTypography variant="h4" fontWeight="500">
          견적 정보
        </CustomizedTypography>
        {!isCreatePurchaseOrderPage && (
          <CustomizedButton
            style={{
              paddingRight: 0
            }}
            variant="text"
            color="dark"
            startIcon={<Download color={colorSet.gray900} size={20} />}
            onClick={() => {
              onDownloadPdf('quotation', quotationId ?? '', projectName ?? '');
            }}
          >
            견적서 다운로드
          </CustomizedButton>
        )}
      </Stack>

      <Box className={classes.tableRoot}>
        <Grid
          container
          className={classes.tableHeader}
          wrap="nowrap"
          justifyContent="space-between"
        >
          {[
            { title: '항목', width: '448px', align: 'left' },
            { title: '금액', width: '152px', align: 'right' }
          ].map((column, i) => (
            <Grid item key={i}>
              <CustomizedTypography
                align={column.align as PropTypes.Alignment}
                variant="subtitle2"
                color="textSecondary"
              >
                {column.title}
              </CustomizedTypography>
            </Grid>
          ))}
        </Grid>

        {items.map((item, i) => {
          return (
            <Grid container key={i} className={classes.tableData} wrap="nowrap">
              <Grid
                alignItems={noDrawing ? 'center' : 'flex-start'}
                container
                wrap="nowrap"
              >
                <CustomizedTypography
                  variant="body2"
                  color="textSecondary"
                  className="index"
                >
                  {i + 1}
                </CustomizedTypography>
                {item.material && (
                  <img
                    alt="thumbnail"
                    src={
                      item.fileId
                        ? AppConfig.fileApiUrl +
                          `/cloud/` +
                          item.fileId +
                          '.png'
                        : Assets.DefaultThumbnail
                    }
                    onError={(e) => {
                      e.currentTarget.src = Assets.DefaultThumbnail;
                    }}
                    className="thumbnail"
                  />
                )}
                <Box marginLeft={2} maxWidth={'calc(100% - 100px)'}>
                  <CustomizedTypography
                    variant="body1"
                    color="textPrimary"
                    gutterBottoms={4}
                    className="fileName"
                  >
                    {item.fileName}
                  </CustomizedTypography>
                  {item.material && (
                    <CustomizedTypography
                      variant="body2"
                      color="textSecondary"
                      fontWeight="normal"
                      gutterBottoms={2}
                      className={'material'}
                    >
                      재료:{' '}
                      {requestEnumToString(
                        item.material as CapaQuotationDetailedMaterialType
                      )}
                    </CustomizedTypography>
                  )}
                  {!noDrawing && (
                    <>
                      <CustomizedTypography
                        variant="body2"
                        color="textSecondary"
                        gutterBottoms={2}
                      >
                        수량: {item.quantity.toLocaleString('kr')}개
                      </CustomizedTypography>
                      <CustomizedTypography
                        variant="body2"
                        color="textSecondary"
                      >
                        단가:{' '}
                        {`${formatCurrency(
                          Number(item.cost / item.quantity)
                        )}원`}
                      </CustomizedTypography>
                    </>
                  )}
                </Box>
              </Grid>

              <Box maxWidth={94}>
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                  align="right"
                  style={{ wordBreak: 'break-all' }}
                >
                  {'총 ' + formatCurrency(Number(item.cost)) + '원'}
                </CustomizedTypography>
              </Box>
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuotatonViewItemsTable;
